/**
 * User Agent utilities for getting clean, structured browser/device information
 */

// Known bot/headless browser patterns
const BOT_PATTERNS = [
  'HeadlessChrome',
  'Puppeteer',
  'PhantomJS',
  'Selenium',
  'WebDriver',
  'Googlebot',
  'AdsBot-Google',
  'PlayStore-Google',
  'bingbot',
  'Baiduspider',
  'YandexBot',
  'DuckDuckBot',
  'Slurp',
  'facebookexternalhit',
  'meta-externalagent',
  'meta-webindexer',
  'Twitterbot',
  'LinkedInBot',
  'WhatsApp',
  'TelegramBot',
  'Discordbot',
  'Applebot',
  'AhrefsBot',
  'SemrushBot',
  'MJ12bot',
  'DotBot',
  'PetalBot',
  'SEBot-WA',
  'PTST/',
  'curl',
  'wget',
  'python-requests',
  'axios',
  'node-fetch',
  'Go-http-client',
  'Java/',
  'libwww',
  'httpunit',
  'httrack',
  'Apache-HttpClient',
];

// Map NT versions to Windows versions
const WINDOWS_VERSIONS: Record<string, string> = {
  '10.0': '10/11',
  '6.3': '8.1',
  '6.2': '8',
  '6.1': '7',
  '6.0': 'Vista',
  '5.1': 'XP',
};

interface UserAgentInfo {
  raw: string;
  clean: string;
  isBot: boolean;
  botName?: string;
}

/**
 * Get clean user agent information
 * Uses navigator.userAgentData if available (Chromium 90+),
 * falls back to regex parsing of navigator.userAgent,
 * returns bot/headless info if navigator is unavailable
 */
export function getUserAgentInfo(): UserAgentInfo {
  // No navigator = SSR or headless environment
  if (typeof navigator === 'undefined') {
    return {
      raw: '',
      clean: 'Server/SSR (No navigator)',
      isBot: true,
      botName: 'SSR',
    };
  }

  const ua = navigator.userAgent || '';

  // Empty user agent = likely headless/bot
  if (!ua.trim()) {
    return {
      raw: '',
      clean: 'Unknown (Empty UA)',
      isBot: true,
      botName: 'Unknown',
    };
  }

  // Check if it's a known bot
  const botMatch = BOT_PATTERNS.find(pattern =>
    ua.toLowerCase().includes(pattern.toLowerCase())
  );
  if (botMatch) {
    return {
      raw: ua,
      clean: `Bot/${botMatch}`,
      isBot: true,
      botName: botMatch,
    };
  }

  // Try modern userAgentData API (Chromium 90+, not Safari/Firefox)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userAgentData = (navigator as any).userAgentData;
  if (userAgentData) {
    try {
      const { brands, mobile, platform } = userAgentData;
      // Filter out the "Not A;Brand" fake entries
      const realBrand = brands?.find(
        (b: { brand: string }) =>
          !b.brand.includes('Not') && !b.brand.includes('Chromium')
      );
      const browser = realBrand?.brand || 'Chrome';
      const version = realBrand?.version || brands?.[0]?.version || '';
      const deviceType = mobile ? 'Mobile' : 'Desktop';

      return {
        raw: ua,
        clean: `${browser}/${version} (${platform}; ${deviceType})`,
        isBot: false,
      };
    } catch {
      // Fall through to regex parsing
    }
  }

  // Fallback: Parse user agent string with regex
  const clean = parseUserAgentString(ua);

  return {
    raw: ua,
    clean,
    isBot: false,
  };
}

/**
 * Parse user agent string to extract browser and OS info
 */
function parseUserAgentString(ua: string): string {
  // Extract browser
  let browser = 'Unknown';

  // Order matters - check more specific patterns first
  if (ua.includes('Edg/')) {
    browser = ua.match(/Edg\/[\d.]+/)?.[0]?.replace('Edg', 'Edge') || 'Edge';
  } else if (ua.includes('OPR/') || ua.includes('Opera')) {
    browser = ua.match(/OPR\/[\d.]+/)?.[0]?.replace('OPR', 'Opera') || 'Opera';
  } else if (ua.includes('Chrome/') && !ua.includes('Chromium')) {
    browser = ua.match(/Chrome\/[\d.]+/)?.[0] || 'Chrome';
  } else if (ua.includes('Firefox/')) {
    browser = ua.match(/Firefox\/[\d.]+/)?.[0] || 'Firefox';
  } else if (ua.includes('Safari/') && ua.includes('Version/')) {
    // Safari has Version/X.X before Safari/X.X
    browser = 'Safari/' + (ua.match(/Version\/([\d.]+)/)?.[1] || '');
  } else if (ua.includes('MSIE') || ua.includes('Trident/')) {
    browser = ua.match(/MSIE [\d.]+/)?.[0] || 'IE/11';
  }

  // Extract OS
  let os = 'Unknown OS';

  if (ua.includes('iPhone') || ua.includes('iPad')) {
    const iosVersion = ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') || '';
    const device = ua.includes('iPad') ? 'iPad' : 'iPhone';
    os = `iOS ${iosVersion} (${device})`;
  } else if (ua.includes('Android')) {
    os = ua.match(/Android [\d.]+/)?.[0] || 'Android';
  } else if (ua.includes('Mac OS X')) {
    const macVersion =
      ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.') || '';
    os = `macOS ${macVersion}`;
  } else if (ua.includes('Windows NT')) {
    const ntVersion = ua.match(/Windows NT ([\d.]+)/)?.[1] || '';
    os = `Windows ${WINDOWS_VERSIONS[ntVersion] || ntVersion}`;
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('CrOS')) {
    os = 'ChromeOS';
  }

  return `${browser}; ${os}`;
}

/**
 * Get just the clean user agent string (convenience function)
 */
export function getCleanUserAgent(): string {
  return getUserAgentInfo().clean;
}

/**
 * Headless automation that spoofs a normal Chrome user agent, so BOT_PATTERNS
 * cannot see it. September 2026: a crawler rotating through ~700 Guangdong
 * China Telecom/Mobile/Unicom IPs (plus proxies in SG/US/VN/BD/BR…) rendered
 * our pages as "Chrome/Windows" with random versions, always at a fixed
 * 1280x1200 screen — a size no real monitor uses. It made up ~2/3 of website
 * sessions (0s active, 1 page) and ~2.5x real GA users. Client-only check.
 */
export function isKnownAutomationClient(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  return (
    window.screen?.width === 1280 &&
    window.screen?.height === 1200 &&
    /Windows NT/.test(navigator.userAgent || '')
  );
}

/**
 * Check if current user agent is a bot/crawler/headless browser
 */
export function isBot(): boolean {
  return getUserAgentInfo().isBot || isKnownAutomationClient();
}
